import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faDownload, faCheck, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface ExtractedAudioPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDownload: () => void;
  onUseAudio: () => void;
  audioUrl: string | null;
  isLoading?: boolean;
}

const ExtractedAudioPreviewModal: React.FC<ExtractedAudioPreviewModalProps> = ({
  isOpen,
  onClose,
  onDownload,
  onUseAudio,
  audioUrl,
  isLoading = false
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState('0:00');
  const [currentTime, setCurrentTime] = useState('0:00');
  const [progress, setProgress] = useState(0);

  if (!isOpen || !audioUrl) return null;

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      const mins = Math.floor(audioRef.current.duration / 60);
      const secs = Math.floor(audioRef.current.duration % 60);
      setDuration(`${mins}:${secs.toString().padStart(2, '0')}`);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const mins = Math.floor(audioRef.current.currentTime / 60);
      const secs = Math.floor(audioRef.current.currentTime % 60);
      setCurrentTime(`${mins}:${secs.toString().padStart(2, '0')}`);
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current) {
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = percent * audioRef.current.duration;
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const modalContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        zIndex: 999999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#2d2d2d',
          padding: '30px',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '600px',
          boxShadow: '0 8px 16px rgba(0,0,0,0.5)',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            color: '#e0e0e0',
            cursor: 'pointer'
          }}
          onClick={onClose}
        >
          ×
        </button>

        {/* Title */}
        <h2 style={{ margin: '0 0 24px 0', color: '#e0e0e0', fontSize: '20px' }}>
          Preview Extracted Audio
        </h2>

        {/* Audio Player */}
        <div
          style={{
            backgroundColor: '#1f1f1f',
            padding: '20px',
            borderRadius: '6px',
            marginBottom: '24px'
          }}
        >
          <audio
            ref={audioRef}
            src={audioUrl}
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
          />

          {/* Play/Pause Button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <button
              onClick={handlePlayPause}
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                border: 'none',
                color: 'white',
                fontSize: '18px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLButtonElement).style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.backgroundColor = '#3b82f6';
              }}
            >
              <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </button>

            {/* Time Display */}
            <div style={{ color: '#e0e0e0', fontSize: '14px' }}>
              {currentTime} / {duration}
            </div>
          </div>

          {/* Progress Bar */}
          <div
            onClick={handleProgressClick}
            style={{
              width: '100%',
              height: '6px',
              backgroundColor: '#444',
              borderRadius: '3px',
              cursor: 'pointer',
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                backgroundColor: '#3b82f6',
                transition: 'width 0.1s linear'
              }}
            />
          </div>
        </div>

        {/* Info Text */}
        <p
          style={{
            margin: '0 0 24px 0',
            color: '#aaa',
            fontSize: '13px',
            lineHeight: '1.5',
            textAlign: 'center'
          }}
        >
          Preview the extracted audio before uploading. You can download it or proceed with uploading to the editor.
        </p>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}
        >
          {/* Cancel Button */}
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #555',
              color: '#e0e0e0',
              padding: '10px 20px',
              fontSize: '14px',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isLoading ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                const target = e.target as HTMLButtonElement;
                target.style.backgroundColor = '#333';
                target.style.borderColor = '#888';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                const target = e.target as HTMLButtonElement;
                target.style.backgroundColor = 'transparent';
                target.style.borderColor = '#555';
              }
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
            Cancel
          </button>

          {/* Download Button */}
          <button
            onClick={onDownload}
            disabled={isLoading}
            style={{
              backgroundColor: '#444',
              border: '1px solid #666',
              color: '#e0e0e0',
              padding: '10px 20px',
              fontSize: '14px',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isLoading ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                const target = e.target as HTMLButtonElement;
                target.style.backgroundColor = '#555';
                target.style.borderColor = '#888';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                const target = e.target as HTMLButtonElement;
                target.style.backgroundColor = '#444';
                target.style.borderColor = '#666';
              }
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
            Download
          </button>

          {/* Use Audio Button */}
          <button
            onClick={onUseAudio}
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? '#3b82f6' : '#3b82f6',
              border: '1px solid #2563eb',
              color: 'white',
              padding: '10px 24px',
              fontSize: '14px',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              opacity: isLoading ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                const target = e.target as HTMLButtonElement;
                target.style.backgroundColor = '#2563eb';
                target.style.borderColor = '#1d4ed8';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                const target = e.target as HTMLButtonElement;
                target.style.backgroundColor = '#3b82f6';
                target.style.borderColor = '#2563eb';
              }
            }}
          >
            <FontAwesomeIcon 
              icon={isLoading ? faSpinner : faCheck} 
              spin={isLoading}
            />
            {isLoading ? 'Uploading...' : 'Use This Audio'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ExtractedAudioPreviewModal;
